import React from "react";

import not from "ramda/src/not";
import prop from "ramda/src/prop";
import ifElse from "ramda/src/ifElse";
import compose from "ramda/src/compose";

import Api from "../../services/Api";
import useAuth from "../../routes/login/AuthContext";
import {
  useNetworkContext,
  useSelectedNetworkContext,
} from "../../contexts/NetworkContext/context";
import { ReadOnlyWrapper } from "../ReadOnlyWrapper";
import { useSnackbar } from "../../hooks/useSnackbar";

import { Switch } from "../Switch";

interface IProps {
  l2Id: number;
  defaultValue: boolean;
  isDisabled?: boolean;
}

const getLabel = (isEnabled: boolean) => (isEnabled ? "Enabled" : "Disabled");

export const StatusSwitch: React.FC<IProps> = (props) => {
  const { getAuthToken, isSuperAdminRead } = useAuth();
  const { showErrorSnackbar, showSuccessSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = React.useState(false);
  const [value, setValue] = React.useState(props.defaultValue);
  const { updateSubNetworkValues } = useNetworkContext();
  const { selectedNetworkId } = useSelectedNetworkContext();

  React.useEffect(() => {
    setValue(props.defaultValue);
  }, [props.defaultValue]);

  const updateLocalNetworkData = React.useCallback(
    (previouslyKnownEnabled: boolean) => () => {
      updateSubNetworkValues(selectedNetworkId, props.l2Id, {
        previouslyKnownEnabled,
      });
    },
    [selectedNetworkId, props.l2Id, updateSubNetworkValues]
  );

  const stopLoading = () => setIsLoading(false);

  const handleError = () =>
    showErrorSnackbar("An error occurred. Please try again");

  const onComplete = () => showSuccessSnackbar("Saved Changes");

  const handleSuccess = (previouslyKnownEnabled: boolean) =>
    ifElse(
      prop("success"),
      compose(onComplete, updateLocalNetworkData(previouslyKnownEnabled)),
      handleError
    );

  const handleChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
    previouslyKnownEnabled: boolean
  ) => {
    const body = {
      subnetworkCommands: [
        {
          l1Id: selectedNetworkId,
          l2Id: props.l2Id,
          previouslyKnownEnabled,
        },
      ],
      unitTypeCommands: [],
    };

    setIsLoading(true);
    setValue(not);

    Api.setMathingRules(selectedNetworkId, body, await getAuthToken())
      .then(handleSuccess(previouslyKnownEnabled))
      .catch(handleError)
      .finally(stopLoading);
  };

  return (
    <ReadOnlyWrapper>
      <Switch
        isDisabled={isLoading || isSuperAdminRead() || props.isDisabled}
        label={getLabel(value)}
        switchProps={{ checked: value, onChange: handleChange }}
      />
    </ReadOnlyWrapper>
  );
};
